//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  BulkCreateOrderResponse,
  CreatorsCampaignsCreateOrderResponse,
  CreatorsCampaignsOrderDetails,
  CreatorsCampaignsOrderProductsDetailsApiResponse,
  CreatorsCampaignsOrdersListResponse,
} from '@apis/creators-campaigns/creators-orders/CreatorOrdersApiModel';
import { isDefined } from '@utils/filterPredicates';
import { Serialized } from '@utils/types/Serialized';

export const deserializeCreatorsCampaignsOrderDetailsData = (
  response: Serialized<
    CreatorsCampaignsOrderDetails<CreatorsCampaignsOrderProductsDetailsApiResponse>
  >,
): CreatorsCampaignsOrderDetails => {
  const serializeOrderEventTimestamp = (
    key: 'fulfilledAt' | 'orderCreatedAt' | 'statusUpdatedAt',
  ) => {
    if (
      isDefined(key) &&
      isDefined(response.eventsTimestamp) &&
      isDefined(response.eventsTimestamp[key])
    ) {
      return new Date(response.eventsTimestamp[key]);
    }
  };

  return {
    ...response,
    eventsTimestamp: {
      orderCreatedAt: serializeOrderEventTimestamp('orderCreatedAt'),
      fulfilledAt: serializeOrderEventTimestamp('fulfilledAt'),
      statusUpdatedAt: serializeOrderEventTimestamp('statusUpdatedAt'),
    },
    createdAt: isDefined(response.createdAt)
      ? new Date(response.createdAt)
      : undefined,
    orderStatusUpdatedAt: isDefined(response.orderStatusUpdatedAt)
      ? new Date(response.orderStatusUpdatedAt)
      : undefined,
    products: isDefined(response.products)
      ? response.products.map((product) => ({
          ...product,
          imageUrl: product.image,
          updatedAt: isDefined(product.updatedAt)
            ? new Date(product.updatedAt)
            : undefined,
        }))
      : [],
  };
};

export const deserializeCreatorsCampaignsLatestOrderData = (
  response: Serialized<CreatorsCampaignsOrdersListResponse>,
): CreatorsCampaignsOrdersListResponse => {
  return {
    ...response,
    data: response.data?.map((order) => ({
      ...order,
      createdOn: isDefined(order.createdOn)
        ? new Date(order.createdOn)
        : undefined,
    })),
  };
};

export const deserializeCreatorsCampaignsCreatedOrderData = (
  response: Serialized<CreatorsCampaignsCreateOrderResponse>,
): CreatorsCampaignsCreateOrderResponse => {
  return {
    ...response,
    createdAt: isDefined(response.createdAt)
      ? new Date(response.createdAt)
      : undefined,
  };
};

export const deserializeBulkCreateOrderResponse = (
  response: Serialized<{ data: BulkCreateOrderResponse }>,
): { data: BulkCreateOrderResponse } => {
  return {
    data: response.data.map((result) => ({
      ...result,
      createdAt: isDefined(result.createdAt)
        ? new Date(result.createdAt)
        : undefined,
    })),
  };
};
